import React, { useState } from 'react';
import axios from '../api/axios';
import { toast } from 'react-toastify';
import { Mail, Send, AlertCircle, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {AxiosError} from "axios";
import { EMAIL_INPUT_PATTERN, isValidEmail } from '@/utils/formValidation';

export default function ForgotPasswordForm() {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess(false);

        if (!isValidEmail(email)) {
            const message = 'Informe um e-mail válido, como usuario@dominio.com.br.';
            setError(message);
            toast.error(message);
            return;
        }

        setLoading(true);
        try {
            await axios.post("/auth/forgot-password", { email: email.trim() });
            setSuccess(true);
            toast.success("E-mail de recuperação enviado! Verifique sua caixa de entrada.");
        } catch (error) {
            const axiosError = error as AxiosError;
            if (axiosError.response) {
                if (axiosError.response.status === 400) {
                    const message = (axiosError.response.data as { message?: string })?.message || 'E-mail inválido.';
                    setError(message);
                    toast.error(message);
                } else {
                    const message = 'Ocorreu um erro ao enviar o e-mail. Por favor, tente novamente.';
                    setError(message);
                    toast.error(message);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-shell">
            <div className="pointer-events-none absolute -left-20 -top-20 h-80 w-80 rounded-full bg-blue-600/10 blur-[120px]" />
            <div className="pointer-events-none absolute -bottom-20 -right-20 h-72 w-72 rounded-full bg-cyan-500/5 blur-[100px]" />
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="auth-card"
            >
                <Link to="/" className="mb-6 flex items-center gap-1 text-sm font-bold text-blue-400 transition-colors duration-200 hover:text-blue-300">
                    <ArrowLeft size={20} /> Voltar para o Login
                </Link>

                <div className="flex flex-col items-center mb-8">
                    <img
                        src="/prefeitura_logo.png"
                        alt="Prefeitura Municipal Logo"
                        className="w-20 h-20 mb-4"
                    />
                    <h2 className="app-page-title text-center text-3xl font-black">
                        Recuperar Senha
                    </h2>
                    <p className="text-slate-500 mt-2 text-center">
                        Digite seu e-mail para receber um link de redefinição.
                    </p>
                </div>

                {success && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="feedback-success mb-6 flex items-center gap-2"
                    >
                        <span>E-mail enviado com sucesso. Verifique sua caixa de entrada.</span>
                    </motion.div>
                )}

                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="feedback-error mb-6 flex items-center gap-2"
                    >
                        <AlertCircle size={20} className="flex-shrink-0" />
                        <span>{error}</span>
                    </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-slate-300 font-medium mb-1 sr-only">E-mail</label>
                        <div className="relative">
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">
                                <Mail size={20} />
                            </span>
                            <input
                                type="email"
                                pattern={EMAIL_INPUT_PATTERN}
                                title="Informe um e-mail válido, como usuario@dominio.com.br."
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="auth-input pl-10"
                                placeholder="E-mail"
                                required
                            />
                        </div>
                    </div>
                    <div>
                        <button
                            type="submit"
                            disabled={loading || success}
                            className="ui-button-primary w-full py-4"
                        >
                            {loading ? (
                                "CARREGANDO"
                            ) : (
                                <>
                                    <Send size={20} />
                                    Enviar link
                                </>
                            )}
                        </button>
                    </div>
                </form>

                <div className="text-center mt-8 text-slate-500 text-sm">
                    © <a href="https://portosoftware.com.br/" target="_blank" rel="noreferrer" className="hover:text-blue-600 transition-colors duration-200">
                    {new Date().getFullYear()} PortoSoftware
                </a>
                </div>
            </motion.div>
        </div>
    );
}
